// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import {
    HasProductContext,
    ProductContext,
    ProductContextModel,
    withProductContext,
} from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import { getVariantChildrenDataView } from "@insite/client-framework/Store/Data/Products/ProductsSelectors";
import { ProductListViewType } from "@insite/client-framework/Store/Pages/ProductList/ProductListState";
import ProductAddToListLink, {
    ProductAddToListLinkStyles,
} from "@insite/content-library/Components/ProductAddToListLink";
import ProductPrice, { ProductPriceStyles } from "@insite/content-library/Components/ProductPrice";
import ProductQuantityBreakPricing, {
    ProductQuantityBreakPricingStyles,
} from "@insite/content-library/Components/ProductQuantityBreakPricing";
import ProductUnitOfMeasureSelect from "@insite/content-library/Components/ProductUnitOfMeasureSelect";
import { ButtonPresentationProps } from "@insite/mobius/Button";
import { SelectPresentationProps } from "@insite/mobius/Select";
import { TextFieldProps } from "@insite/mobius/TextField";
import { TypographyPresentationProps } from "@insite/mobius/Typography";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import ProductOriginally from "@keller/App/Components/Product/ProductOriginally";
import ProductPreviousPurchase from "@keller/App/Components/Product/ProductPreviousPurchase";
import { getIsNonPurchasable, xIsAuthenticated } from "@keller/App/Store/Selectors/GeneralSelectors";
import { ProductAvailabilityStyles } from "@keller/Overrides/Components/ProductAvailability";
import ProductContextAvailability from "@keller/Overrides/Components/ProductContextAvailability";
import AddToCartActionSection from "@keller/Overrides/Widgets/ProductList/AddToCartActionsSection";
import ProductListProductImage, {
    ProductListProductImageStyles,
} from "@keller/Overrides/Widgets/ProductList/ProductListProductImage";
import { xClassNames, xScreen } from "@keller/XDL/Utilities";
import React, { FC, useMemo } from "react";
import { connect, ResolveThunks } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    showPrice: boolean;
    showAddToList: boolean;
    // XNG-Changes
    showCompare: boolean;
    showAvailability: boolean;
    showPreviousPurchase: boolean;
    view?: ProductListViewType;
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    const isNonPurchasable = getIsNonPurchasable(state);
    const isAuthenticated = xIsAuthenticated(state);

    return {
        productSettings: getSettingsCollection(state).productSettings,
        // XNG-Changes
        showInventoryAvailability: getSettingsCollection(state).productSettings.showInventoryAvailability,
        isNonPurchasable,
        isAuthenticated,
        // Compare & AddToList section
        canShowActionsSection: ownProps.showAddToList || ownProps.showCompare,
    };
};

const mapDispatchToProps = {};

type Props = ReturnType<typeof mapStateToProps> &
    HasProductContext &
    ResolveThunks<typeof mapDispatchToProps> &
    OwnProps;

export interface ProductListActionsStyles {
    wrapper?: InjectableCss;
    addToCartWrapper?: InjectableCss;
    addToCartButton?: ButtonPresentationProps;
    quantityOrdered?: TextFieldProps;
    unitOfMeasureSelect?: SelectPresentationProps;
    quantityBreakPricing?: ProductQuantityBreakPricingStyles;
    price?: ProductPriceStyles;
    addToListLink?: ProductAddToListLinkStyles;
    addToListWrapper?: InjectableCss;
    // XNG-Changes
    productOriginallyStyles?: TypographyPresentationProps;
    availabilityStyles?: ProductAvailabilityStyles;
    previousPurchaseAndAvailabilityWrapper?: InjectableCss;
    productPreviousPurchase?: TypographyPresentationProps;
    compareStyles?: ProductListProductImageStyles;
}

export const actionsStyles: ProductListActionsStyles = {
    wrapper: {
        css: css`
            display: flex;
            width: 100%;
            flex-direction: column;

            &.grid {
                margin-top: auto;
                padding-top: 14px;
            }
            &.list.variant-product {
                height: 100%;
            }
        `,
    },
    unitOfMeasureSelect: {
        cssOverrides: {
            formField: css`
                margin-top: 20px;
            `,
        },
    },
    quantityBreakPricing: {
        viewLink: {
            css: css`
                margin: 8px 0;
            `,
        },
    },
    addToCartWrapper: {
        css: css`
            display: flex;
            align-items: flex-start;
            flex-basis: 100%;
            margin-bottom: 10px;

            &.grid {
                width: 100%;
                flex-basis: auto;
                margin-bottom: 0;
            }
            &.variant-product {
                margin: auto 0;
            }
        `,
    },
    productOriginallyStyles: {
        css: css`
            margin-bottom: 6px;
        `,
    },
    price: {
        wrapper: {
            css: css`
                margin-bottom: 10px;
            `,
        },
    },
    previousPurchaseAndAvailabilityWrapper: {
        css: css`
            margin-bottom: 12px;
            display: flex;
            justify-content: space-between;
            flex-wrap: nowrap;
            gap: 8px;
            &:empty {
                display: none;
            }
        `,
    },
    addToListWrapper: {
        css: css`
            margin-top: 14px;
            min-height: 16px;
            display: flex;
            justify-content: space-between;
            & div:first-child {
                margin-right: 8px;
            }
        `,
    },
    availabilityStyles: {
        container: {
            css: css`
                display: flex;
                flex-wrap: wrap;
            `,
        },
    },
    productPreviousPurchase: {
        css: css`
            flex-shrink: 0;
        `,
    },
};

const styles = actionsStyles;

const ProductListActions: FC<Props> = ({
    productContext,
    productSettings,
    showPrice,
    showAddToList,
    showAvailability,
    showInventoryAvailability,
    showPreviousPurchase,
    canShowActionsSection,
    showCompare,
    isAuthenticated,
    view,
}) => {
    const isVariantProduct = productContext.product.isVariantParent;

    // Check if all variant traits have a selected value
    const variantSelection = useAppSelector(state =>
        isVariantProduct
            ? ((state as any).xProductListVariants?.variantSelectionsByProductId?.[productContext.product.id] ?? {})
            : {},
    );
    const isVariantSelectionComplete = useMemo(() => {
        if (!isVariantProduct || !productContext.product.variantTraits) {
            return true;
        }
        return productContext.product.variantTraits.every(trait => !!variantSelection[trait.id]);
    }, [isVariantProduct, productContext.product.variantTraits, variantSelection]);

    // Read loaded variant children so we can resolve the specific selected variant product
    const variantChildren = useAppSelector(state =>
        getVariantChildrenDataView(state, isVariantProduct ? productContext.product.id : undefined).value,
    );

    // Several shared components (ProductContextAvailability, ProductAddToListLink) key off of
    // product.isVariantParent / product.canAddToWishlist, both of which are only meaningful for a real
    // purchasable SKU, not the variant parent. Resolve the actual selected variant product so that,
    // once selection is complete, availability and add-to-wishlist use the variant's real ProductModel
    // (mirrors how the PDP swaps in the selected variant's product for pricing/availability/wishlist).
    const selectedVariant = useMemo(() => {
        if (!isVariantSelectionComplete || !variantChildren) {
            return undefined;
        }
        return variantChildren.find(child =>
            child.childTraitValues?.every(ctv => variantSelection[ctv.styleTraitId] === ctv.id),
        );
    }, [isVariantSelectionComplete, variantChildren, variantSelection]);

    const selectedVariantContext: ProductContextModel | undefined = useMemo(() => {
        if (!selectedVariant) {
            return undefined;
        }
        return { ...productContext, product: selectedVariant };
    }, [productContext, selectedVariant]);

    const canShowPrice = showPrice && (!isVariantProduct || isVariantSelectionComplete);
    const canShowAvailability =
        showAvailability && showInventoryAvailability && (!isVariantProduct || isVariantSelectionComplete);
    const classNameValues = xClassNames.append([view?.toLowerCase() || "", isVariantProduct ? "variant-product" : ""]);

    // XNG-Changes: Update structure
    return (
        <StyledWrapper {...styles.wrapper} className={classNameValues}>
            {canShowPrice && (
                <>
                    <ProductOriginally
                        productContext={productContext}
                        extendedStyles={styles.productOriginallyStyles}
                    />
                    <ProductPrice
                        product={productContext}
                        showLabel={false}
                        showSavings={false}
                        showSavingsAmount={productSettings.showSavingsAmount}
                        showSavingsPercent={productSettings.showSavingsPercent}
                        extendedStyles={styles.price}
                    />
                    <ProductQuantityBreakPricing extendedStyles={styles.quantityBreakPricing} />
                </>
            )}
            <ProductUnitOfMeasureSelect extendedStyles={styles.unitOfMeasureSelect} />
            {canShowAvailability && (
                <StyledWrapper {...styles.previousPurchaseAndAvailabilityWrapper}>
                    {selectedVariantContext ? (
                        <ProductContext.Provider value={selectedVariantContext}>
                            <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                        </ProductContext.Provider>
                    ) : (
                        <ProductContextAvailability extendedStyles={styles.availabilityStyles} />
                    )}
                    {showPreviousPurchase && (
                        <ProductPreviousPurchase
                            product={productContext.product}
                            extendedStyles={styles.productPreviousPurchase}
                        />
                    )}
                </StyledWrapper>
            )}
            <StyledWrapper {...styles.addToCartWrapper} className={classNameValues}>
                <AddToCartActionSection productContext={productContext} />
            </StyledWrapper>
            {canShowActionsSection && (
                <StyledWrapper {...styles.addToListWrapper}>
                    {showAddToList &&
                        (selectedVariantContext ? (
                            // XNG-Changes: for variant products, only show Add to Wishlist when the user is
                            // logged in - matches the behavior products without variants already get from
                            // product.canAddToWishlist (left untouched below), applied explicitly here since
                            // the resolved variant child's own canAddToWishlist isn't reliably auth-aware.
                            isAuthenticated && (
                                <ProductContext.Provider value={selectedVariantContext}>
                                    <ProductAddToListLink
                                        data-test-selector={`actionsAddToList${selectedVariant!.id}`}
                                        extendedStyles={styles.addToListLink}
                                    />
                                </ProductContext.Provider>
                            )
                        ) : (
                            <ProductAddToListLink
                                data-test-selector={`actionsAddToList${productContext.product.id}`}
                                extendedStyles={styles.addToListLink}
                            />
                        ))}
                    <ProductListProductImage
                        showImage={false}
                        showBrands={false}
                        showCompare={showCompare}
                        extendedStyles={styles.compareStyles}
                    />
                </StyledWrapper>
            )}
        </StyledWrapper>
    );
};

export default connect(mapStateToProps, mapDispatchToProps)(withProductContext(ProductListActions));
